import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Sidebar } from 'primeng/primeng';
import { IStrategy } from '../../../models/IStrategy';
import { StrategiesService } from '../../services/strategies.service';
import { InstitutionService } from '../../../auth/services/institution.service';
import { ValidationService } from '../../../auth/services/validation.service';
import { timer } from 'rxjs/observable/timer';
import { map, filter, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-strategy-details',
  templateUrl: './strategy-details.component.html',
  styleUrls: ['./strategy-details.component.scss']
})
export class StrategyDetailsComponent implements OnInit {
  public readonly MAX_NAME_LENGTH = 80;

  @Output() create = new EventEmitter<IStrategy>();
  @Output() createError = new EventEmitter<Error>();
  @Output() edit = new EventEmitter<{edited: IStrategy, pristine: IStrategy}>();
  @Output() editError = new EventEmitter<Error>();
  @Output() cancel = new EventEmitter<void>();

  strategyForm: FormGroup;
  startDateValue: Date;
  endDateValue: Date;
  institutionId: number;
  isEditMode = false;
  private strategy: IStrategy;
  private pristineStrategy: IStrategy;

  constructor(private formBuilder: FormBuilder,
              private datePipe: DatePipe,
              private institutionService: InstitutionService,
              private strategiesService: StrategiesService,
              private validationService: ValidationService) {
  }

  ngOnInit() {
    this.institutionId = this.institutionService.institutionId;
    this.createForm();
  }

  get name() {
    return this.strategyForm.get('name');
  }

  get startDate() {
    return this.strategyForm.get('startDate');
  }

  get endDate() {
    return this.strategyForm.get('endDate');
  }

  get headerContent() {
    return (this.isEditMode) ? 'Edit Plan' : 'New Plan';
  }

  get showStrategyNameErrors() {
    return this.name.invalid && (this.name.dirty || this.name.touched);
  }

  get showDateErrors() {
    return this.endDate.dirty && this.endDate.invalid;
  }

  get isSubmitDisabled() {
    return this.isPristine || this.hasDateErrors || this.name.value.length === 0;
  }

  get remainingCharacters() {
    return this.MAX_NAME_LENGTH - (this.name.value ? this.name.value.length : 0);
  }

  private get isPristine() {
    return this.name.pristine && this.startDate.pristine && this.endDate.pristine;
  }

  get hasDateErrors() {
    return this.startDate.errors || this.endDate.errors;
  }

  onShowSidebar(strategySidebar: Sidebar) {
    strategySidebar.maskClickListener();
  }

  onHideSidebar() {
    this.strategy = undefined;
    this.pristineStrategy = undefined;
    this.strategyForm.reset();
    this.isEditMode = false;
  }

  onEditStrategy(strategy: IStrategy) {
    this.isEditMode = true;
    this.strategy = strategy;
    this.pristineStrategy = strategy; // preserve a copy of the strategy for comparison
    this.strategyForm.patchValue({
      name: strategy.strategyName,
      startDate: strategy.startDate ? new Date(this.datePipe.transform(strategy.startDate, 'MM/dd/yyyy')) : undefined,
      endDate: strategy.endDate ? new Date(this.datePipe.transform(strategy.endDate, 'MM/dd/yyyy')) : undefined
    });
  }

  onSubmit() {
    if (this.isEditMode) {
      this.strategiesService.editStrategy(this.strategy.strategyId, this.name.value, this.startDateValue, this.endDateValue)
        .subscribe(strategy =>
          this.edit.emit({edited: strategy, pristine: this.pristineStrategy}),  // both edited and un-edited strategies
          error => this.editError.emit(error));
    } else {
      this.strategy = this.prepareSaveStrategy();
      this.strategiesService.createStrategy(this.name.value, this.startDateValue, this.endDateValue, this.institutionId)
        .subscribe(strategy => this.create.emit(strategy), error => this.createError.emit(error));
    }
  }

  private createForm() {
    this.strategyForm = this.formBuilder.group({
      name: ['', Validators.required, (control) => this.validateName(control)],
      startDate: [''],
      endDate: ['']
    }, {
      validator: this.validationService.validateDatesInOrder('startDate', 'endDate')
    });
  }

  private validateName(control: AbstractControl) {
    return timer(750) // wait 3/4 of a second
      .pipe(
        map(() => control.value), // get the current name
        filter(strategyName => !!strategyName), // throw away empty strings
        filter(strategyName => !this.pristineStrategy || strategyName !== this.pristineStrategy.strategyName),
        distinctUntilChanged(), // make sure it changed
        switchMap(strategyName => this.strategiesService.name(this.institutionId, strategyName)),
        map(it => it && it.duplicateName ? it : null),
        catchError((error: any) => {
          console.log(error);
          return null;
        })
      );
  }

  private prepareSaveStrategy(): IStrategy {
    const formModel = this.strategyForm.value;
    return {strategyName: formModel.name as string, startDate: formModel.startDate as Date, endDate: formModel.endDate as Date};
  }
}
